import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type ThemeMode = "light" | "dark";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
  toggle: () => {},
});

/**
 * Theme root. Holds the light/dark context the app relies on and mirrors it to
 * the document: the `.dark` class drives Tailwind's dark variant + the shadcn
 * tokens, and `data-theme` is kept for any legacy attribute selectors. Portaled
 * overlays (dialogs, toasts) inherit the tokens because both flags live on <html>.
 */
export function ThemeProvider({
  defaultTheme = "light",
  children,
}: {
  defaultTheme?: ThemeMode;
  children: ReactNode;
}) {
  const [theme, setTheme] = useState<ThemeMode>(defaultTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.setAttribute("data-theme", theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggle: () => setTheme((t) => (t === "light" ? "dark" : "light")) }),
    [theme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div className="az-root" data-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
